import { zodResolver } from "@hookform/resolvers/zod";
import {
  useForm,
  UseFormReturn,
  FieldValues,
  useFieldArray,
} from "react-hook-form";
import { z } from "zod";
import LoadingButton from "@/components/LoadingButton";
import FormDatePicker from "@/components/FormDatePicker";
import { convertDollarsToCents, convertCentsToDollars } from "@/lib/utils";
import { Form } from "@/components/ui/form";
import FormInput from "@/components/FormInput";
import FormTextarea from "@/components/FormTextarea";
import FormSelectMultiple from "@/components/FormSelectMultiple";
import { getFormattedSources } from "@/util/getFormattedSources";
import { slugify } from "@/util/slugify";
import { PurchaseWithItems, UserSettingsWithSources } from "@/types";
import { useToast } from "@/components/ui/use-toast";
import { useTanFetch } from "@/hooks/useTanFetch";
import useTanMutate from "@/hooks/useTanMutate";
import {
  Pencil2Icon,
  LockClosedIcon,
  CheckCircledIcon,
} from "@radix-ui/react-icons";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";
import TooltipWrapper from "@/components/TooltipWrapper";

const itemSchema = z.object({
  id: z.string(),
  itemName: z.string().min(2, { message: "An item name is required" }).max(256),
  itemNotes: z.string().max(5000).nullable(),
  listingId: z.string().nullable(),
  isListed: z.boolean(),
  isSold: z.boolean(),
  listedSource: z.string().nullable(),
  soldDate: z.string().nullable(),
});

const editPurchaseFormSchema = z.object({
  purchaseNotes: z.string().max(5000),
  purchaseName: z.string().min(1, { message: "A name is required" }),
  datePurchased: z.date().or(z.string()),
  priceTotal: z.string().min(1),
  priceTax: z.string().min(1),
  priceShipping: z.string().min(1),
  priceFees: z.string().min(1),
  source: z
    .array(z.object({ label: z.string(), value: z.string() }))
    .min(1, { message: "A source is required" }),
  sourceUrl: z.string().or(z.literal("")),
  items: z.array(itemSchema),
});

export default function EditPurchaseForm({
  data,
  closeModal,
}: {
  data: PurchaseWithItems;
  closeModal: () => void;
}) {
  const { toast } = useToast();
  const [editingItems, setEditingItems] = useState<Record<string, boolean>>({});
  const { data: userSettings } = useTanFetch<UserSettingsWithSources>(
    ["settings"],
    true,
  );
  const { mutate: trigger, isPending: purchasesLoading } = useTanMutate({
    key: "purchases",
    method: "put",
    reval: ["purchases"],
  });

  const { mutateAsync: updateItem, isPending: itemsLoading } = useTanMutate({
    key: "items",
    method: "put",
    reval: ["purchases", "items"],
  });

  const formattedSources = getFormattedSources(
    userSettings as UserSettingsWithSources,
  );

  const form = useForm<z.infer<typeof editPurchaseFormSchema>>({
    resolver: zodResolver(editPurchaseFormSchema),
    defaultValues: {
      purchaseNotes: data?.purchaseNotes || "",
      purchaseName: data.purchaseName,
      datePurchased: new Date(data.datePurchased).toISOString(),
      priceTotal: convertCentsToDollars(data.priceTotal).toFixed(2),
      priceTax: convertCentsToDollars(data.priceTax).toFixed(2),
      priceShipping: convertCentsToDollars(data.priceShipping).toFixed(2),
      priceFees: convertCentsToDollars(data.priceFees).toFixed(2),
      source: data.source
        ? [{ label: data.source, value: slugify(data.source) }]
        : [],
      sourceUrl: data.sourceUrl || "",
      items: data.items.map((item) => ({
        id: item.id,
        itemName: item.itemName,
        itemNotes: item.itemNotes,
        listingId: item.listingId,
        isListed: item.isListed,
        isSold: item.isSold,
        listedSource: item.listedSource,
        soldDate: item.soldDate,
      })),
    },
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: "items",
  });

  const toggleItemEdit = (itemId: string) => {
    setEditingItems((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const cancelItemEdit = (itemId: string, index: number) => {
    // Reset the form values to original
    const originalItem = data.items[index];
    form.setValue(`items.${index}.itemName`, originalItem.itemName);
    form.setValue(`items.${index}.itemNotes`, originalItem.itemNotes || "");
    toggleItemEdit(itemId);
  };

  const saveItem = async (index: number, showToast = true) => {
    const item = form.getValues(`items.${index}`);
    const originalItem = data.items.find((i) => i.id === item.id);
    const fieldId = fields[index].id; // Get the field ID for the edit state

    if (!originalItem) return;

    // Close the edit view immediately (optimistic update)
    setEditingItems((prev) => ({ ...prev, [fieldId]: false }));

    try {
      await updateItem({
        ...originalItem,
        itemName: item.itemName,
        itemNotes: item.itemNotes,
      });

      if (showToast) {
        toast({
          description: `${item.itemName} updated successfully`,
          variant: "success",
        });
      }

      return true;
    } catch {
      // Reopen the edit view if the update failed
      setEditingItems((prev) => ({ ...prev, [fieldId]: true }));

      if (showToast) {
        toast({
          description: "Error updating item",
          variant: "destructive",
        });
      }
      return false;
    }
  };

  const onSubmit = async (values: z.infer<typeof editPurchaseFormSchema>) => {
    // First, save any items that are currently being edited
    const editingItemIds = Object.keys(editingItems).filter(
      (id) => editingItems[id],
    );
    if (editingItemIds.length > 0) {
      const savePromises = editingItemIds.map((itemId) => {
        const index = values.items.findIndex((item) => item.id === itemId);
        if (index !== -1) {
          return saveItem(index, false);
        }
        return Promise.resolve(true);
      });

      const results = await Promise.all(savePromises);
      if (results.some((result) => !result)) {
        toast({
          description: "Some items could not be saved. Please try again.",
          variant: "destructive",
        });
        return;
      }
    }

    const { items: _, ...purchaseValues } = values;
    const purchaseData = {
      ...data,
      ...purchaseValues,
      datePurchased: new Date(purchaseValues.datePurchased).toISOString(),
      priceTotal: convertDollarsToCents(Number(purchaseValues.priceTotal)),
      priceTax: convertDollarsToCents(Number(purchaseValues.priceTax)),
      priceShipping: convertDollarsToCents(
        Number(purchaseValues.priceShipping),
      ),
      priceFees: convertDollarsToCents(Number(purchaseValues.priceFees)),
      source: purchaseValues.source[0].label,
    };

    try {
      trigger(purchaseData);
      toast({
        description: `${values.purchaseName} updated`,
        variant: "success",
      });
      closeModal();
    } catch {
      toast({
        description: "There was an error updating this purchase",
        variant: "destructive",
      });
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="max-w-(--breakpoint-sm) space-y-4"
      >
        <div className="flex justify-between gap-4">
          <FormInput form={form} label="Purchase name" id="purchaseName" />
          <FormDatePicker
            form={form}
            label="Date purchased"
            id="datePurchased"
            placeholder="Pick a date"
            classes=""
          />
        </div>

        <div className="flex justify-between gap-4">
          <FormInput label="Total Price" id="priceTotal" form={form} />
          <FormInput label="Tax" id="priceTax" form={form} />
          <FormInput label="Shipping" id="priceShipping" form={form} />
          <FormInput label="Fees" id="priceFees" form={form} />
        </div>

        <div className="flex gap-4">
          <FormSelectMultiple
            id="source"
            label="Source"
            maxSelected={1}
            creatable
            defaultOptions={formattedSources}
            placeholder="Select or add a source"
            form={form as unknown as UseFormReturn<FieldValues>}
          />

          <FormInput
            itemStyles="grow"
            label="Source URL"
            id="sourceUrl"
            form={form}
          />
        </div>

        {/* Item notes */}
        <FormTextarea id="purchaseNotes" label="Notes" form={form} />

        {/* Items Section */}
        {fields.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Items</Label>
            <div className="rounded-md max-h-80 overflow-y-auto">
              <div className="pr-4 space-y-3">
                {fields.map((field, index) => {
                  const isEditable = !field.listingId;
                  const isEditing = editingItems[field.id];

                  return (
                    <div
                      key={field.id}
                      className="flex items-center gap-3 text-sm px-3 py-2 rounded-lg bg-muted/50"
                    >
                      <div className="flex-1 space-y-2">
                        {isEditing && isEditable ? (
                          <>
                            <Input
                              {...form.register(`items.${index}.itemName`)}
                              placeholder="Item name"
                              className="h-9"
                            />
                            <Textarea
                              {...form.register(`items.${index}.itemNotes`)}
                              placeholder="Item notes (optional)"
                              className="min-h-[60px] resize-none"
                            />
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">
                                {form.watch(`items.${index}.itemName`)}
                              </span>
                              {/* {field.isListed && ( */}
                              {/*   <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"> */}
                              {/*     Listed on {field.listedSource || "platform"} */}
                              {/*   </span> */}
                              {/* )} */}
                              {/* {field.isSold && ( */}
                              {/*   <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"> */}
                              {/*     Sold{" "} */}
                              {/* {field.soldDate ? `on ${field.soldDate}` : ""} */}
                              {/*   </span> */}
                              {/* )} */}
                            </div>
                            {form.watch(`items.${index}.itemNotes`) && (
                              <p className="text-sm text-muted-foreground">
                                {form.watch(`items.${index}.itemNotes`)}
                              </p>
                            )}
                          </>
                        )}
                      </div>

                      <div className="">
                        {isEditable ? (
                          isEditing ? (
                            <div className="flex flex-col items-start justify-start">
                              <Button
                                // size="icon"
                                variant="ghost"
                                onClick={() => saveItem(index)}
                                disabled={itemsLoading}
                                className="hover:!bg-transparent text-success/80 hover:text-success"
                              >
                                <CheckCircledIcon className="h-4 w-4" />
                                Save
                              </Button>
                              <Button
                                // size="icon"
                                variant="ghost"
                                onClick={() => cancelItemEdit(field.id, index)}
                                className="hover:!bg-transparent text-destructive/80 hover:text-destructive"
                              >
                                <X className="h-4 w-4" />
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => toggleItemEdit(field.id)}
                              className="h-8 w-8 p-0"
                            >
                              <Pencil2Icon className="h-4 w-4" />
                            </Button>
                          )
                        ) : (
                          <div className="h-8 w-8 flex items-center justify-center text-muted-foreground">
                            <TooltipWrapper
                              asChild
                              trigger={<LockClosedIcon className="h-4 w-4" />}
                              content={
                                <div className="">
                                  {field.isListed &&
                                    `Listed on ${field.listedSource}`}
                                  {field.soldDate &&
                                    `Sold on ${field.soldDate}`}
                                </div>
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="w-full text-right">
          <LoadingButton
            label="Submit"
            type="submit"
            loading={purchasesLoading}
            disabled={!form.formState.isDirty}
          />
        </div>
      </form>
    </Form>
  );
}
